import timeZoneMapper from '@bingads-webui-component-legacy/timezonemapper/index';
import decimal from '@bingads-webui-component-legacy/humanize/decimal';
import _ from 'underscore';
import {
  bidStrategyOptionKeys, collectCriterions, getAudienceAds, isAdFormatValid, isAudienceAdsValid,
} from '@bingads-webui-campaign/hotel-common-upsert';
import {
  isDeviceTargetValid,
  isLocationValid,
  convertLocationStateToLocationSchemaCampaign,
} from '@bingads-webui-campaign/hotel-common-targets';
import { isCampaignNameValid } from '@bingads-webui-campaign-react/wizard-shared-components';
import { bidStrategyTypeValueMap, isBidStrategyValid, fields as bidStrategyFields } from './components/bid-strategy/utility';
import { isBudgetValid } from './components/budget/utility';
import { fieldsToCheck } from './components';
import { convertDeviceStateToDeviceSchema } from './get-targets-criterion';

export function getUnSupportedBidAdjustmentFeatures(CampaignBidStrategyType) {
  switch (CampaignBidStrategyType) {
    case 'Commission':
      return ['adSchedule'];
    default:
      return [];
  }
}

export function getAdSchedule(campaign) {
  const targets = campaign.adScheduleData;
  return targets.map((criterion) => {
    const Status = _.isEmpty(criterion) ? 'Active' : criterion.Status;
    if (campaign.Id) {
      return {
        ...criterion,
        Status,
        Campaign: {
          Id: campaign.Id,
        },
      };
    }
    return {
      ...criterion,
      Status,
      CampaignName: campaign.name.value,
    };
  });
}

export function getAdGroupBid(values) {
  const {
    bidStrategy,
  } = values;

  switch (bidStrategy.option) {
    case bidStrategyOptionKeys.ManualCpc:
      return {
        SearchBid: bidStrategy[bidStrategyFields.AdGroupBid] && decimal.fromLocalToFloat(bidStrategy[bidStrategyFields.AdGroupBid]),
      };
    case bidStrategyOptionKeys.PercentCpc:
      return {
        AdGroupPercentCpcBid: bidStrategy[bidStrategyFields.AdGroupPercentBid] && decimal.fromLocalToFloat(bidStrategy[bidStrategyFields.AdGroupPercentBid]),
      };
    case bidStrategyOptionKeys.Commission:
      return {
        AdGroupCommissionRate: bidStrategy[bidStrategyFields.AdGroupCommissionRate] && decimal.fromLocalToFloat(bidStrategy[bidStrategyFields.AdGroupCommissionRate]),
      };
    default:
      return {
        SearchBid: bidStrategy[bidStrategyFields.AdGroupBid] && decimal.fromLocalToFloat(bidStrategy[bidStrategyFields.AdGroupBid]),
      };
  }
}

function getName(campaign) {
  const Name = { Name: campaign.name.value };
  return Name;
}

function getBiddingScheme(campaign) {
  const bidStrategyType = campaign.bidStrategy.option;
  const bidOrPercentValue = campaign.bidStrategy[bidStrategyType];
  const bidStrategyField = bidStrategyTypeValueMap[bidStrategyType];
  const BiddingScheme = bidStrategyType === bidStrategyOptionKeys.ManualCpc ? { Type: bidStrategyType } : { Type: bidStrategyType, [bidStrategyField]: bidOrPercentValue };
  if (bidStrategyField !== bidStrategyTypeValueMap.ManualCpc) {
    BiddingScheme[bidStrategyField] = bidOrPercentValue && decimal.fromLocalToFloat(bidOrPercentValue);
  }
  return { BiddingScheme };
}

function getBudget(campaign) {
  const Budget = {
    Amount: campaign.budget.amount && decimal.fromLocalToFloat(campaign.budget.amount),
    Type: 'DailyStandard',
  };
  return { Budget };
}

function getAdScheduleUseSearcherTimeZone(campaign) {
  const { AdScheduleUseSearcherTimeZone } = campaign;
  return { AdScheduleUseSearcherTimeZone };
}

export function convertCampaignStateToCampaignJSONSchemaData(campaign, initTargets) {
  // could reference mt-entity-tapi or client-data package
  const AdScheduleUseSearcherTimeZone = getAdScheduleUseSearcherTimeZone(campaign);
  const Name = getName(campaign);
  const BiddingScheme = getBiddingScheme(campaign);
  const Budget = getBudget(campaign);
  const deviceSettings = convertDeviceStateToDeviceSchema(campaign, initTargets);
  const locationTargets = convertLocationStateToLocationSchemaCampaign(campaign.location, campaign.bundleLocationCriterions, campaign);
  const adschedules = getAdSchedule(campaign);
  const AdGroups = campaign.AdGroups ? { AdGroups: campaign.AdGroups } : {};
  const unSupportedFeatures = getUnSupportedBidAdjustmentFeatures(campaign.bidStrategy.option);
  const audienceAds = getAudienceAds(campaign.audienceAds);
  const jsonSchemaData = {
    ...Name,
    ...AdScheduleUseSearcherTimeZone,
    ...BiddingScheme,
    ...Budget,
    ...audienceAds,
    CampaignType: 'Hotel',
    Languages: ['All'], // Hotel Campaign support All language by default
    TimeZone: timeZoneMapper.getAccountTimeZone(),
    TrackingUrlTemplate: campaign.urlOptions.TrackingUrlTemplate,
    FinalUrlSuffix: campaign.urlOptions.FinalUrlSuffix,
    UrlCustomParameters: campaign.urlOptions.UrlCustomParameters,
    ...AdGroups,
    CampaignCriterions: [
      ...deviceSettings,
      ...locationTargets,
      ...collectCriterions('adSchedule', unSupportedFeatures, adschedules),
    ],
  };

  if (campaign.Id) {
    return {
      Id: campaign.Id,
      ...jsonSchemaData,
    };
  }
  return {
    Status: 'Active',
    ...jsonSchemaData,
  };
}

export function validateCampaignState(campaign, serverContext, isCreation, patchErrMsg) {
  return {
    name: isCampaignNameValid(campaign.name.value),
    budget: isBudgetValid({ value: campaign.budget.amount, serverContext }),
    bidStrategy: isBidStrategyValid({ bidStrategy: campaign.bidStrategy, isCreation, serverContext }),
    audienceAds: isAudienceAdsValid(campaign.audienceAds),
    device: campaign.device.useParentLevel ? true : isDeviceTargetValid(campaign.device.currentLevelSettings, patchErrMsg, campaign.bidStrategy.option),
    location: isLocationValid(campaign.location, campaign.bidStrategy.option),
    adFormat: isAdFormatValid(campaign.adFormat) || !isCreation,
  };
}

export function generateDataToInvalidState(validationData, campaign) {
  let data = { ...campaign };
  fieldsToCheck.forEach((field) => {
    if (validationData[field] === false) {
      data = {
        ...data,
        [field]: {
          ...data[field],
          isInvalid: true,
        },
      };
    }
  });
  return data;
}
